import React, { useContext, useState } from 'react';
import { subDays, addDays, addHours, startOfToday, isAfter, isBefore } from 'date-fns';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import Isvg from 'react-inlinesvg';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ReservationStatus } from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';

import { Card } from 'components/Card';
import { Button } from 'components/Form';
import Tabs, { Tab, TabLabel } from 'components/Tabs';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import Roster, { SORT_OPTION } from 'components/Roster';
import When from 'components/When';
import { PFE_ICONS } from 'constants/icons';
import { PERMISSION } from 'constants/logic';
import { scheduleInsightsPropTypes } from 'constants/propTypes';
import URLS from 'constants/urls';
import { Title, Controls, Control } from 'features/App';
import { selectUserHasPermission } from 'selectors/general';

import { selectExcessInventoryState } from '../Home/modules/ExcessInventory/ExcessInventory.selectors';
import VenueSchedulesContext from './Contexts/VenueSchedulesContext';
import EmptyDetail from './EmptyDetail';
import EmptyRoster from './EmptyRoster';
import { ClassReservations, HistoricalUtilization } from './Insights';
import RosterListItem from './RosterListItem';
import {
  actOnOpportunityFromSchedule,
  redirectToManageClass,
  redirectToManageSchedule,
  addSpot,
} from './YieldManagement.saga';
import { selectActiveRoster } from './YieldManagement.selectors';

import {
  ScheduleDetailHeaderMobile,
  ReservationsOverview,
  ClassInfoMobile,
  ClassInfoDesktop,
  ScheduleDetailActions,
  ClassInsightsCalloutBanner,
} from './ScheduleDetail/index';
import { reservationsOverviewMessages } from './ScheduleDetail/ScheduleDetail.messages';
import './ScheduleDetail.scss';

const messages = {
  tab: defineMessages({
    roster: {
      id: 'yield-management.schedule-detail.tab-roster',
      defaultMessage: 'Roster',
    },
    insights: {
      id: 'yield-management.schedule-detail.tab-insights',
      defaultMessage: 'Insights',
    },
  }),
  banner: defineMessages({
    campaign: {
      id: 'yield-management.schedule-detail.banner-campaign',
      defaultMessage: 'Attract members and boost your reservations by offering a Campaign.',
    },
    campaignCta: {
      id: 'yield-management.schedule-detail.cta-campaign',
      defaultMessage: 'Create campaign',
    },
    smartspot: {
      id: 'yield-management.schedule-detail.banner-smartspot',
      defaultMessage: '<b>SmartSpot</b> will ensure your prime spots are always protected for direct clients.',
    },
    hasDemand: {
      id: 'yield-management.schedule-list.schedule-list-item.has-demand',
      defaultMessage:
        '{value, plural, one {<b># client</b> is waiting} other {<b># clients</b> are waiting}} to be notified when spots open up',
    },
  }),
};

const ScheduleDetail = props => {
  const {
    cannotCancelClass,
    cancelSchedule,
    canToggleClassSmartSpot,
    canOnlyToggleNonRecurring,
    canViewRates,
    currencyCode,
    emailMember,
    emailScheduleAttendees,
    insights,
    insightsIsLoaded,
    isDesktop,
    isMsInventoryVenue,
    isReservationsStoppable,
    noDirectFillData,
    openCancelScheduleModal,
    openModal,
    rosterIsLoaded,
    shouldShowCancelRecurring,
    startAllowingReservations,
    stopAllowingReservations,
    tab,
    updateReservationAttendance,
    venueId,
    venueName,
    venueSubtitle,
    waiverId,
    waiverVersionId,
    showExcessInventory,
  } = props;

  const dispatch = useDispatch();
  const hasSpotManagePermission = useSelector(state =>
    selectUserHasPermission(state, PERMISSION.DISPLAY_SPOT_MANAGEMENT)
  );

  const { activeSchedule: schedule } = useContext(VenueSchedulesContext);
  const {
    apiGuid,
    className,
    reservationsStopped,
    scheduleClass,
    scheduleId,
    isDisabled: scheduleIsDisabled,
    isInThePast: scheduleIsInThePast,
    startDate,
    startDateTime,
    endDateTime,
    isFull,
    isInThePast,
    isLocked,
    classpassSpotsUnfilled,
    smartspotEnabled,
    opportunity = {},
    spots = {},
  } = schedule;
  const { classId } = scheduleClass || {};
  const { classpassDemand, classpassTotal } = spots;

  const [opportunityActedOn, setOpportunityActedOn] = useState(false);

  const handleRedirectToManageClass = () => {
    dispatch(redirectToManageClass(venueId, classId));
  };

  const handleRedirectToManageSchedule = () => {
    dispatch(redirectToManageSchedule({ apiGuid, startDate, startDateTime, venueId }));
  };

  const midnightTwoDaysAgo = subDays(startOfToday(), 2);
  const hourFromNow = addHours(new Date(), 1);
  const isScheduleBeforeUpdateAttendanceTimeRange = isBefore(startDateTime, midnightTwoDaysAgo);
  const isScheduleAfterUpdateAttendanceTimeRange = isAfter(startDateTime, hourFromNow);
  const canUpdateAttendance = !isScheduleBeforeUpdateAttendanceTimeRange && !isScheduleAfterUpdateAttendanceTimeRange;
  const sixHoursAfterScheduleEnd = addHours(endDateTime, 6);
  const fourteenDaysAfterScheduleEnd = addDays(endDateTime, 14);
  const canPerformScheduleActions = isBefore(new Date(), sixHoursAfterScheduleEnd);
  const isInExtendedActionsWindow =
    isAfter(new Date(), sixHoursAfterScheduleEnd) && isBefore(new Date(), fourteenDaysAfterScheduleEnd);

  const roster = useSelector(state => selectActiveRoster(state, schedule));

  const printRosterHref = roster ? URLS.schedule.print(venueId, scheduleId, startDate) : null;

  const shouldCountReservation = reservation =>
    [ReservationStatus.PROCESSED, ReservationStatus.QUEUED].includes(reservation.reservationStatus);
  const shouldCountNewReservation = reservation =>
    shouldCountReservation(reservation) && reservation.visitNumberToVenue === 1;

  const { reservationsCount, newReservationsCount } = roster.reduce(
    (memo, reservation) => ({
      reservationsCount: memo.reservationsCount + (shouldCountReservation(reservation) ? 1 : 0),
      newReservationsCount: memo.newReservationsCount + (shouldCountNewReservation(reservation) ? 1 : 0),
    }),
    {
      reservationsCount: 0,
      newReservationsCount: 0,
    }
  );
  const hasReservations = reservationsCount > 0;
  const hasNewReservations = newReservationsCount > 0;

  const showScheduleContent = !scheduleIsDisabled || (scheduleIsInThePast && roster.length > 0);

  const excessInventoryData = useSelector(state => selectExcessInventoryState(state, schedule?.scheduleId));
  // This variable differs from the one above, because it takes the whole class (not schedule) into consideration.
  const classHasReservations = excessInventoryData?.data?.classPassSpots > 0 || false;

  const handleTabChange = tabValue => {
    const { setSelectedTab } = props;
    setSelectedTab(tabValue);
  };

  const recommendation = opportunity ? opportunity.recommendation : false;
  const showOpportunityButton =
    hasSpotManagePermission &&
    recommendation &&
    !isInThePast &&
    !isLocked &&
    !opportunityActedOn &&
    !reservationsStopped &&
    !smartspotEnabled;
  const showAddSpotButton =
    hasSpotManagePermission &&
    !isFull &&
    !isInThePast &&
    !isLocked &&
    (classpassSpotsUnfilled === 0 || classpassSpotsUnfilled === Infinity) &&
    (smartspotEnabled || !recommendation);

  const showDemand = classpassDemand > 0 && (showAddSpotButton || showOpportunityButton);

  const handleActOnOpportunityFromSchedule = () => {
    dispatch(
      actOnOpportunityFromSchedule({
        venueId,
        schedule,
        opportunities: opportunity ? [opportunity] : [],
        spotsAdded: opportunity && opportunity.recommendation,
        className,
        startDateTime,
        source: 'schedule detail',
        showDemand,
      })
    );
  };

  const ADD_SPOT_VALUE = 1;
  const handleAddSpot = () => {
    dispatch(addSpot(classpassTotal, scheduleId, classpassTotal + ADD_SPOT_VALUE, smartspotEnabled, showDemand));
  };

  const handleActOnOpportunity = event => {
    event.preventDefault();
    handleActOnOpportunityFromSchedule();
    setOpportunityActedOn(true);
  };

  return (
    <div className="schedule-detail">
      {!isDesktop && (
        <Title>
          <ScheduleDetailHeaderMobile schedule={schedule} />
        </Title>
      )}

      {!isDesktop && (canPerformScheduleActions || isInExtendedActionsWindow) && (
        <Controls>
          <Control rightButton>
            <ScheduleDetailActions
              cannotCancelClass={cannotCancelClass}
              className="ml-auto"
              cancelSchedule={cancelSchedule}
              isDesktop={isDesktop}
              reservationsStopped={reservationsStopped}
              isInThePast={scheduleIsInThePast}
              openModal={openModal}
              schedule={schedule}
              reservationsCount={reservationsCount}
              newReservationsCount={newReservationsCount}
              hasReservations={hasReservations}
              hasNewReservations={hasNewReservations}
              shouldShowCancelRecurring={shouldShowCancelRecurring}
              stopAllowingReservations={stopAllowingReservations}
              startAllowingReservations={startAllowingReservations}
              emailScheduleAttendees={emailScheduleAttendees}
              isReservationsStoppable={isReservationsStoppable}
              isInExtendedActionsWindow={isInExtendedActionsWindow}
              venueName={venueName}
              venueSubtitle={venueSubtitle}
              isMsInventoryVenue={isMsInventoryVenue}
              openCancelScheduleModal={openCancelScheduleModal}
              redirectToManageClass={handleRedirectToManageClass}
              redirectToManageSchedule={handleRedirectToManageSchedule}
            />
          </Control>
        </Controls>
      )}

      {!isDesktop && (
        <Card className="schedule-detail__header" flush collapse>
          <ClassInfoMobile
            schedule={schedule}
            canToggleClassSmartSpot={canToggleClassSmartSpot}
            canOnlyToggleNonRecurring={canOnlyToggleNonRecurring}
          />
          <When predicate={showDemand}>
            <div className="schedule-detail__banner">
              <Isvg src={PFE_ICONS.notification} />
              <Message
                message={messages.banner.hasDemand}
                values={{ value: classpassDemand, b: chunk => <strong>{chunk}</strong> }}
              />
              {showAddSpotButton && (
                <Button
                  className="schedule-detail__add-spot-button bt--sm bt--text-sm"
                  styled="primary-outline"
                  onClick={handleAddSpot}>
                  <Message message={reservationsOverviewMessages.ss.ssAddSpotButton} />
                </Button>
              )}
              {showOpportunityButton && (
                <Button
                  className="schedule-detail__ss-opportunity bt--sm bt--text-sm"
                  styled="primary-outline"
                  onClick={handleActOnOpportunity}>
                  <Message message={reservationsOverviewMessages.opportunityButton} values={{ recommendation }} />
                </Button>
              )}
            </div>
          </When>
        </Card>
      )}

      {isDesktop && (
        <Card className="schedule-detail__header" flush collapse>
          <ClassInfoDesktop
            schedule={schedule}
            canToggleClassSmartSpot={canToggleClassSmartSpot}
            canOnlyToggleNonRecurring={canOnlyToggleNonRecurring}
          />

          <When predicate={showExcessInventory && !classHasReservations && !showDemand}>
            <div className="schedule-detail__banner">
              <Isvg src={PFE_ICONS.opportunitiesFill} />
              <Message message={messages.banner.campaign} />
              <Link to={`/campaigns/${venueId}/add`}>
                <Message message={messages.banner.campaignCta} />
              </Link>
            </div>
          </When>

          <When predicate={showExcessInventory && classHasReservations && !showDemand}>
            <div className="schedule-detail__banner">
              <Isvg src={PFE_ICONS.smartSpotShield} />
              <Message message={messages.banner.smartspot} values={{ b: chunk => <strong>{chunk}</strong> }} />
            </div>
          </When>

          <When predicate={showDemand}>
            <div className="schedule-detail__banner" data-cypress="schedule-detail-waiting-banner">
              <Isvg src={PFE_ICONS.notification} />
              <Message
                message={messages.banner.hasDemand}
                values={{ value: classpassDemand, b: chunk => <strong>{chunk}</strong> }}
              />
              {showAddSpotButton && (
                <Button
                  className="schedule-detail__add-spot-button bt--sm bt--text-sm"
                  styled="primary-outline"
                  onClick={handleAddSpot}>
                  <Message message={reservationsOverviewMessages.ss.ssAddSpotButton} />
                </Button>
              )}
              {showOpportunityButton && (
                <Button
                  className="schedule-detail__ss-opportunity bt--sm bt--text-sm"
                  styled="primary-outline"
                  onClick={handleActOnOpportunity}>
                  <Message message={reservationsOverviewMessages.opportunityButton} values={{ recommendation }} />
                </Button>
              )}
            </div>
          </When>

          {showScheduleContent && <ReservationsOverview schedule={schedule} />}
        </Card>
      )}

      {showExcessInventory && <ClassInsightsCalloutBanner />}

      {scheduleIsDisabled && !showScheduleContent && (
        <EmptyDetail isDisabled isInThePast={scheduleIsInThePast} schedule={schedule} />
      )}

      {!isDesktop && showScheduleContent && (
        <Card flush>
          <ReservationsOverview schedule={schedule} />
        </Card>
      )}

      {showScheduleContent && (
        <Tabs
          className="schedule-detail__tabs"
          selectedTab={tab}
          onTabChange={handleTabChange}
          tabListProps={{ className: 'schedule-detail__tabs-list' }}>
          <Tab name="roster" className="schedule-detail__tab">
            <TabLabel>
              <Message className="schedule-detail__tab-label" message={messages.tab.roster} />
            </TabLabel>
            {!rosterIsLoaded ? (
              <LoadingDots />
            ) : (
              <Roster
                className="yield-management__roster"
                name={messages.tab.roster}
                RowComponent={RosterListItem}
                getKey={item => item.reservationId}
                data={roster}
                EmptyComponent={EmptyRoster}
                waiverId={waiverId}
                waiverVersionId={waiverVersionId}
                printHref={printRosterHref}
                sortOptions={[SORT_OPTION.ALPHABETIC, SORT_OPTION.VISITS, SORT_OPTION.RECENT]}
                canUpdateAttendance={canUpdateAttendance}
                isScheduleAfterUpdateAttendanceTimeRange={isScheduleAfterUpdateAttendanceTimeRange}
                isScheduleBeforeUpdateAttendanceTimeRange={isScheduleBeforeUpdateAttendanceTimeRange}
                openModal={openModal}
                updateReservationAttendance={updateReservationAttendance}
                emailMember={emailMember}
                venueName={venueName}
                venueSubtitle={venueSubtitle}
                noScroll
              />
            )}
          </Tab>
          <Tab name="insights" className="schedule-detail__tab">
            <TabLabel>
              <Message className="schedule-detail__tab-label" message={messages.tab.insights} />
            </TabLabel>

            {insightsIsLoaded ? (
              <>
                <div className="yield-management__class-utilization-container">
                  <ClassReservations noDirectFillData={noDirectFillData} schedule={schedule} />
                </div>
                <div className="yield-management__historical-utilization-container">
                  <HistoricalUtilization
                    schedule={schedule}
                    insights={insights}
                    currencyCode={currencyCode}
                    noDirectFillData={noDirectFillData}
                    canViewRates={canViewRates}
                  />
                </div>
              </>
            ) : (
              <LoadingDots />
            )}
          </Tab>
          {isDesktop && (canPerformScheduleActions || isInExtendedActionsWindow) && (
            <Tab name="options" className="ml-auto" isFaux>
              <ScheduleDetailActions
                className="ml-auto"
                cancelSchedule={cancelSchedule}
                cannotCancelClass={cannotCancelClass}
                isDesktop={isDesktop}
                reservationsStopped={reservationsStopped}
                isInThePast={scheduleIsInThePast}
                openModal={openModal}
                reservationsCount={reservationsCount}
                newReservationsCount={newReservationsCount}
                schedule={schedule}
                venueName={venueName}
                venueSubtitle={venueSubtitle}
                hasReservations={hasReservations}
                hasNewReservations={hasNewReservations}
                emailScheduleAttendees={emailScheduleAttendees}
                shouldShowCancelRecurring={shouldShowCancelRecurring}
                stopAllowingReservations={stopAllowingReservations}
                startAllowingReservations={startAllowingReservations}
                isReservationsStoppable={isReservationsStoppable}
                isInExtendedActionsWindow={isInExtendedActionsWindow}
                isMsInventoryVenue={isMsInventoryVenue}
                openCancelScheduleModal={openCancelScheduleModal}
                redirectToManageClass={handleRedirectToManageClass}
                redirectToManageSchedule={handleRedirectToManageSchedule}
              />
            </Tab>
          )}
        </Tabs>
      )}
    </div>
  );
};

ScheduleDetail.propTypes = {
  cannotCancelClass: PropTypes.bool,
  cancelSchedule: PropTypes.func,
  canToggleClassSmartSpot: PropTypes.bool,
  canOnlyToggleNonRecurring: PropTypes.bool,
  canViewRates: PropTypes.bool,
  currencyCode: PropTypes.string,
  emailMember: PropTypes.func,
  emailScheduleAttendees: PropTypes.func,
  insights: PropTypes.shape(scheduleInsightsPropTypes),
  insightsIsLoaded: PropTypes.bool,
  isDesktop: PropTypes.bool,
  isMsInventoryVenue: PropTypes.bool,
  isReservationsStoppable: PropTypes.bool,
  noDirectFillData: PropTypes.bool,
  openCancelScheduleModal: PropTypes.func,
  openModal: PropTypes.func,
  rosterIsLoaded: PropTypes.bool,
  setSelectedTab: PropTypes.func,
  shouldShowCancelRecurring: PropTypes.bool,
  startAllowingReservations: PropTypes.func,
  stopAllowingReservations: PropTypes.func,
  tab: PropTypes.string,
  updateReservationAttendance: PropTypes.func,
  venueId: PropTypes.number,
  venueName: PropTypes.string,
  venueSubtitle: PropTypes.string,
  waiverId: PropTypes.number,
  waiverVersionId: PropTypes.number,
  showExcessInventory: PropTypes.bool,
};

export default ScheduleDetail;
